import PendingCatalogRequests, { CatalogPublicationFilter, usePendingCatalogRequests } from "../catalogApprovals/PendingCatalogRequests";
import useCatalogAccess from "../useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import { useCatalogTaxonomyText } from "../catalogTaxonomyMessages";
import CorporateRecordBadge from "../CorporateRecordBadge";
import InventoryPolicyChip from "../InventoryPolicyChip";
import { managedByAdmin } from "../../utils/tenderManagement";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import AddSubfamilyPopup from "../popups/AddSubfamilyPopup";
import EditTaxonomyPopup from "../popups/EditTaxonomyPopup";
import { TaxonomyMergeActions } from "../popups/TaxonomyMergePopup";
import TaxonomyItemsPopup, {
  makeTaxonomyItemsScope,
  TaxonomyItemsLink,
} from "../popups/TaxonomyItemsPopup";
import {

  buildSearchBlob,
  formatItemsFamiliesDate,
  inventoryPolicyHint,
  inventoryPolicyLabel,
  readItemsFamiliesUser,
  resolveTaxonomyInventoryPolicy,
  truncateItemsFamiliesText,
} from "../itemsFamiliesUi";

function buildSubfamilySearchBlob(subfamily) {
  return buildSearchBlob([
    subfamily?.code,
    subfamily?.name,
    subfamily?.family?.name,
    subfamily?.description,
  ]);
}

export default function SubfamiliesTab({ businessUnitId }) {
  const t = useCatalogTaxonomyText();
  const currentUser = readItemsFamiliesUser();
  const activeBusinessUnitId = businessUnitId || currentUser?.active_business_unit_id;
  const access = useCatalogAccess(activeBusinessUnitId);
  const viewPermission = "purchasing.items.view";
  const editPermissions = ["purchasing.items.create", "purchasing.items.update"];
  const exportPermissions = [viewPermission, "purchasing.items.export"];
  const canViewSubfamilies = access.can(viewPermission);
  const canCreateSubfamily = access.can("purchasing.items.create");
  const canEditSubfamily = access.can(editPermissions);
  const canExport = access.can(exportPermissions);
  const isMobile = useIsMobile();

  const [subfamilies, setSubfamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [publicationFilter, setPublicationFilter] = useState("published");
  const pendingCatalog = usePendingCatalogRequests({ entityType: "subfamily", businessUnitId: activeBusinessUnitId,
    enabled: canViewSubfamilies && publicationFilter === "pending", refreshKey: isAddPopupOpen });
  const [editing, setEditing] = useState(null);
  const [itemsScope, setItemsScope] = useState(null);

  const fetchSubfamilies = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canViewSubfamilies) {
        setSubfamilies([]);
        setLoading(false);
        return;
      }

      if (!quiet) {
        setLoading(true);
      }

      const stamp = access.capture();
      try {
        const options = access.requestConfig(viewPermission);
        const response = await axios.get("/api/purchasing/subfamilies", { ...options, params: { ...options.params, catalog: 1 } });
        if (!access.isCurrent(stamp, viewPermission)) return;
        const sorted = [...(response.data || [])].sort((left, right) => {
          const leftFamily = String(left?.family?.name || "");
          const rightFamily = String(right?.family?.name || "");
          if (leftFamily !== rightFamily) {
            return leftFamily.localeCompare(rightFamily, undefined, { sensitivity: "base" });
          }
          return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
            sensitivity: "base",
          });
        });
        setSubfamilies(sorted);
      } catch (requestError) {
        if (!access.isCurrent(stamp, viewPermission)) return;
        console.error("[SUBFAMILIES][LOAD][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: "We could not load the subfamily list for this business unit.",
        });
      } finally {
        if (!quiet && access.isCurrent(stamp, viewPermission)) {
          setLoading(false);
        }
      }
    },
    [canViewSubfamilies, access, viewPermission]
  );

  useEffect(() => {
    fetchSubfamilies();
  }, [fetchSubfamilies]);

  const filteredSubfamilies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return subfamilies.filter((subfamily) => !query || buildSubfamilySearchBlob(subfamily).includes(query));
  }, [search, subfamilies]);

  const stats = useMemo(() => {
    const visibleFamilies = new Set(
      filteredSubfamilies.map((subfamily) => subfamily?.family?.name).filter(Boolean)
    ).size;
    const described = filteredSubfamilies.filter((subfamily) => String(subfamily?.description || "").trim()).length;

    return {
      visible: filteredSubfamilies.length,
      families: visibleFamilies,
      described,
    };
  }, [filteredSubfamilies]);

  const exportToExcel = async () => {
    if (!access.can(exportPermissions) || publicationFilter === "pending") return;
    const stamp = access.capture();
    try {
      const options = access.requestConfig(exportPermissions);
      const response = await axios.get("/api/purchasing/subfamilies", { ...options, params: { ...options.params, catalog: 1, export: 1 } });
      if (!access.isCurrent(stamp, exportPermissions)) return;
      const query = search.trim().toLowerCase();
      const exportRecords = (response.data || []).filter((row) => !query || buildSubfamilySearchBlob(row).includes(query));
    if (!exportRecords.length) {
      setNotice({
        tone: "info",
        message: "There are no subfamily rows in the current slice to export.",
      });
      return;
    }

    const exportRows = exportRecords.map((subfamily) => ({
      Code: subfamily?.code || "",
      Name: subfamily?.name || "",
      Family: subfamily?.family?.name || "",
      InventoryPolicy: inventoryPolicyLabel(resolveTaxonomyInventoryPolicy(subfamily, "subfamily").policy),
      InventoryPolicySource: inventoryPolicyHint(resolveTaxonomyInventoryPolicy(subfamily, "subfamily"), "subfamily"),
      Description: subfamily?.description || "",
      CreatedAt: subfamily?.created_at || "",
      UpdatedAt: subfamily?.updated_at || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subfamilies");
    XLSX.writeFile(workbook, "subfamilies.xlsx");

    setNotice({
      tone: "success",
      message: `${exportRecords.length} subfamily rows exported to Excel.`,
    });
    } catch (_error) {
      if (access.isCurrent(stamp, exportPermissions)) setNotice({ tone: "error", message: "Could not export this catalog. Please try again." });
    }
  };

  const updateInventoryPolicy = async (subfamily, inventoryPolicy) => {
    if (!access.can(editPermissions) || managedByAdmin(subfamily)) return;
    const stamp = access.capture();
    try {
      const response = await axios.put(`/api/purchasing/subfamilies/${subfamily.id}`, {
        inventory_policy: inventoryPolicy || null,
      }, access.requestConfig(editPermissions));
      if (!access.isCurrent(stamp, editPermissions)) return;
      if (isPendingCatalogCreation(response.data)) {
        setPendingRequest(response.data.request);
        return;
      }
      setSubfamilies((current) =>
        current.map((entry) => (Number(entry.id) === Number(subfamily.id) ? { ...entry, ...response.data } : entry))
      );
      setNotice({ tone: "success", message: `${subfamily?.name || "Subfamily"} inventory policy updated.` });
    } catch (requestError) {
      if (!access.isCurrent(stamp, editPermissions)) return;
      console.error("[SUBFAMILIES][POLICY][ERROR]", requestError);
      setNotice({ tone: "error", message: "Could not update this inventory policy." });
      return false;
    }
  };

  const handleAdded = async (newSubfamily) => {
    await fetchSubfamilies({ quiet: true });
    setNotice({
      tone: "success",
      message: `${newSubfamily?.name || "Subfamily"} created successfully.`,
    });
  };

  const handleEdited = (updated) => {
    setSubfamilies((current) => current.map((entry) => Number(entry.id) === Number(updated.id) ? { ...entry, ...updated } : entry));
    setNotice({ tone: "success", message: `${updated.name} updated successfully.` });
  };

  const renderEditButton = (subfamily) => canEditSubfamily && !managedByAdmin(subfamily) ? (
    <button type="button" className="spx-btn spx-btn--ghost" aria-label={t(`Edit Subfamily: ${subfamily.name}`)} onClick={() => setEditing(subfamily)}>{t("Edit")}</button>
  ) : null;

  if (!canViewSubfamilies) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("You do not have permission to access subfamilies.")}</div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("Loading subfamilies...")}</div>
      </section>
    );
  }

  return (
    <>
      <section className="spx-card pu-taxonomy">


        <div className="spx-head">
          <div className="spx-headMain">
            <div className="spx-kicker">{t("Family → Subfamily → Category → Subcategory → Item")}</div>
            <h2 className="spx-title">{t("Subfamilies")}</h2>
            <p className="spx-subtitle">{t("Each subfamily belongs to a family and groups its categories.")}</p>
          </div>

          <div className="spx-actions">
            <TaxonomyMergeActions kind="subfamily" records={subfamilies} businessUnitId={activeBusinessUnitId} onChanged={() => fetchSubfamilies({ quiet: true })} />
            {canCreateSubfamily ? (
              <button type="button" className="spx-btn spx-btn--primary" onClick={() => setIsAddPopupOpen(true)}>{t("+ Add Subfamily")} </button>
            ) : null}
            {canExport && <button type="button" className="spx-btn spx-btn--warning" disabled={publicationFilter === "pending"} onClick={exportToExcel}>{t("Export Excel")} </button>}
          </div>
        </div>


        <div className="spx-toolbar">
          <div className="ifm-toolbarGrid ifm-toolbarGrid--two">
            <input
              type="search"
              className="spx-search"
              aria-label={t("Search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search by code, subfamily, family or description...")}
            />
            {publicationFilter !== "pending" && <div className="spx-note">{t("Showing")} <strong>{filteredSubfamilies.length}</strong> {t("of")} <strong>{subfamilies.length}</strong> {t("subfamilies.")} </div>}
          </div>
        </div>

        <CatalogPublicationFilter value={publicationFilter} onChange={setPublicationFilter} />

        {notice ? <div className={`spx-banner spx-banner--${notice.tone}`}>{t(notice.message)}</div> : null}

        {publicationFilter === "pending" ? <PendingCatalogRequests entityType="subfamily" businessUnitId={activeBusinessUnitId} state={pendingCatalog} search={search} /> : <>
        <div className="spx-stats pu-taxonomy-stats">
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Visible Subfamilies")}</div>
            <div className="spx-statValue">{stats.visible}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Families Represented")}</div>
            <div className="spx-statValue">{stats.families}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("With Description")}</div>
            <div className="spx-statValue">{stats.described}</div>
          </div>
        </div>

        {!filteredSubfamilies.length ? (
          <div className="spx-empty">{t("No subfamilies match the current search.")}</div>
        ) : isMobile ? (
          <div className="spx-mobileList">
            {filteredSubfamilies.map((subfamily) => (
              <article key={subfamily.id} className="spx-mobileCard">
                {(() => {
                  const policy = resolveTaxonomyInventoryPolicy(subfamily, "subfamily");
                  return (
                    <>
                <div className="spx-mobileTop">
                  <div>
                    <div className="spx-code">{subfamily?.code || `SUBFAMILY-${subfamily.id}`}</div>
                    <TaxonomyItemsLink
                      scope={makeTaxonomyItemsScope("subfamily", subfamily?.id, subfamily?.name)}
                      onOpen={setItemsScope}
                      strong
                      className="spx-strong"
                    >
                      {truncateItemsFamiliesText(subfamily?.name || "-", 48)}<CorporateRecordBadge record={subfamily} />
                    </TaxonomyItemsLink>
                  </div>
                  <InventoryPolicyChip policy={policy.policy} name={subfamily.name} editable={canEditSubfamily && !managedByAdmin(subfamily)} onChange={(value) => updateInventoryPolicy(subfamily, value)} />
                </div>
                    </>
                  );
                })()}

                <div className="ifm-tagRow">
                  <TaxonomyItemsLink
                    scope={makeTaxonomyItemsScope("family", subfamily?.family?.id, subfamily?.family?.name)}
                    onOpen={setItemsScope}
                    className="ifm-tag"
                  >
                    {subfamily?.family?.name || t("No family")}
                  </TaxonomyItemsLink>
                </div>

                <div className="spx-mobileRows">
                  <div>
                    <div className="spx-mobileLabel">{t("Created")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(subfamily?.created_at)}</div>
                  </div>
                  <div>
                    <div className="spx-mobileLabel">{t("Updated")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(subfamily?.updated_at)}</div>
                  </div>
                </div>

                <div className="spx-mobileValue" style={{ marginTop: 10 }}>
                  {subfamily?.description || "-"}
                </div>
                {canEditSubfamily && !managedByAdmin(subfamily) && <div className="spx-actions" style={{ marginTop: 10 }}>{renderEditButton(subfamily)}</div>}
              </article>
            ))}
          </div>
        ) : (
          <div className="spx-tableWrap">
            <table className="spx-table ifm-table--compact">
              <thead>
                <tr>
                  <th>{t("Code")}</th>
                  <th>{t("Subfamily")}</th>
                  <th>{t("Family")}</th>
                  <th>{t("Inventory Policy")}</th>
                  <th>{t("Description")}</th>
                  <th>{t("Created")}</th>
                  <th>{t("Updated")}</th>
                  {canEditSubfamily && <th>{t("Actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSubfamilies.map((subfamily) => (
                  (() => {
                    const policy = resolveTaxonomyInventoryPolicy(subfamily, "subfamily");
                    return (
                      <tr key={subfamily.id}>
                        <td title={subfamily?.code || `SUBFAMILY-${subfamily.id}`}>
                          <span className="spx-code">{subfamily?.code || `SUBFAMILY-${subfamily.id}`}</span>
                        </td>
                        <td title={subfamily?.name || "-"}>
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("subfamily", subfamily?.id, subfamily?.name)}
                            onOpen={setItemsScope}
                            strong
                            className="spx-strong"
                          >
                            {truncateItemsFamiliesText(subfamily?.name || "-", 48)}<CorporateRecordBadge record={subfamily} />
                          </TaxonomyItemsLink>
                        </td>
                        <td className="spx-muted">
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("family", subfamily?.family?.id, subfamily?.family?.name)}
                            onOpen={setItemsScope}
                          >
                            {subfamily?.family?.name || "-"}
                          </TaxonomyItemsLink>
                        </td>
                        <td>
                          <InventoryPolicyChip policy={policy.policy} name={subfamily.name} editable={canEditSubfamily && !managedByAdmin(subfamily)} onChange={(value) => updateInventoryPolicy(subfamily, value)} />
                        </td>
                        <td className="spx-muted" title={subfamily?.description || "-"}>
                          {truncateItemsFamiliesText(subfamily?.description || "-", 68)}
                        </td>
                        <td className="spx-muted">{formatItemsFamiliesDate(subfamily?.created_at)}</td>
                        <td className="spx-muted">{formatItemsFamiliesDate(subfamily?.updated_at)}</td>
                        {canEditSubfamily && <td>{renderEditButton(subfamily)}</td>}
                      </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>}
      </section>

      {pendingRequest && <CatalogCreationPendingNotice request={pendingRequest} onClose={() => setPendingRequest(null)} />}

      <AddSubfamilyPopup
        existingRecords={subfamilies}
        isOpen={isAddPopupOpen}
        onClose={() => setIsAddPopupOpen(false)}
        onAdded={handleAdded}
      />

      {editing && <EditTaxonomyPopup key={editing.id} kind="subfamily" record={editing} existingRecords={subfamilies} onClose={() => setEditing(null)} onSaved={handleEdited} />}

      <TaxonomyItemsPopup scope={itemsScope} onClose={() => setItemsScope(null)} />
    </>
  );
}
